/**
 * EmailLogSection - Display email history for a document
 * PROMPT 41: Financial Communications
 */
import { useState, useEffect } from "react";
import { Mail, Clock, User, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { format } from "date-fns";

export function EmailLogSection({ logs = [], loading = false }) {
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <Card className="bg-surface-elevated/50 border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-surface-elevated rounded w-3/4"></div>
            <div className="h-4 bg-surface-elevated rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card className="bg-surface-elevated/50 border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No emails sent yet</p>
        </CardContent>
      </Card>
    );
  }

  const displayLogs = expanded ? logs : logs.slice(0, 2);

  return (
    <Card className="bg-surface-elevated/50 border-border" data-testid="email-log-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
            <Badge variant="secondary" className="ml-2 bg-surface-elevated text-foreground">
              {logs.length}
            </Badge>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayLogs.map((log, index) => (
          <div
            key={log.id || index}
            className="p-3 bg-surface-elevated rounded-lg border border-border"
            data-testid={`email-log-item-${index}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">
                    {log.subject}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Clock className="h-3 w-3" />
                  <span>
                    {log.sent_at
                      ? format(new Date(log.sent_at), "dd MMM yyyy HH:mm")
                      : "Unknown date"}
                  </span>
                  {log.template_name && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground">
                        Template: {log.template_name}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs text-muted-foreground">To:</span>
                  {log.recipients_to?.map((email, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-xs bg-surface-base border-border text-foreground"
                    >
                      {email}
                    </Badge>
                  ))}
                  {log.recipients_cc?.length > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground ml-2">CC:</span>
                      {log.recipients_cc.map((email, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="text-xs bg-surface-base border-border text-muted-foreground"
                        >
                          {email}
                        </Badge>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <Badge
                className={
                  log.status === "sent"
                    ? "bg-green-900/50 text-green-300 border-green-700"
                    : "bg-red-900/50 text-red-300 border-red-700"
                }
              >
                {log.status === "sent" ? "Sent" : "Failed"}
              </Badge>
            </div>
          </div>
        ))}

        {logs.length > 2 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full text-muted-foreground hover:text-foreground hover:bg-surface-elevated"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                Show {logs.length - 2} More
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default EmailLogSection;
